import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, LinearProgress, Typography } from '@mui/material';
import React, { useCallback, useEffect, useId, useRef } from 'react';

interface IProps {
  title: string;
  onClose?: () => void;
  actions?: React.ReactNode;
  startActions?: React.ReactNode;
  headerContent?: React.ReactNode;
  isCloseDisabled?: boolean;
  isLoading?: boolean;
  children?: React.ReactNode;
}

const ROOT_SX = {
  height: '100%',
  minHeight: 0,
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
  outline: 0,
} as const;

const HEADER_SX = { position: 'relative', flex: '0 0 auto', px: 3, py: 2 } as const;

const PROGRESS_SX = { position: 'absolute', left: 0, right: 0, bottom: 0 } as const;

const BODY_SX = { position: 'relative', flex: '1 1 auto', minHeight: 0, overflowY: 'auto' } as const;

const FOOTER_SX = {
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 1,
  px: 3,
  py: 2,
} as const;

const START_ACTIONS_SX = { display: 'flex', alignItems: 'center', gap: 1 } as const;

const ACTIONS_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  gap: 1,
  ml: 'auto',
} as const;

export const SidebarFormLayout: React.FC<IProps> = ({
  title,
  onClose,
  actions,
  startActions,
  headerContent,
  isCloseDisabled = false,
  isLoading = false,
  children,
}) => {
  const titleId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const body = bodyRef.current;

    if (!body) {
      return;
    }

    if (isLoading) {
      body.setAttribute('inert', '');
    } else {
      body.removeAttribute('inert');
    }
  }, [isLoading]);

  useEffect(() => {
    const root = rootRef.current;
    const opener = document.activeElement;

    root?.focus({ preventScroll: true });

    return () => {
      const active = document.activeElement;
      const isFocusLeftBehind = !active || active === document.body || Boolean(root?.contains(active));

      if (isFocusLeftBehind && opener instanceof HTMLElement && opener !== document.body && opener.isConnected) {
        opener.focus({ preventScroll: true });
      }
    };
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Escape' || e.defaultPrevented || isCloseDisabled) {
        return;
      }

      e.preventDefault();
      onClose?.();
    },
    [isCloseDisabled, onClose],
  );

  return (
    <Box ref={rootRef} role="dialog" aria-labelledby={titleId} tabIndex={-1} sx={ROOT_SX} onKeyDown={handleKeyDown}>
      <Box sx={HEADER_SX}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 40 }}>
          <Typography id={titleId} variant="h6" component="h2" sx={{ m: 0, flex: '1 1 auto', minWidth: 0 }}>
            {title}
          </Typography>
          <IconButton
            aria-label="Close"
            title="Close"
            onClick={onClose}
            disabled={isCloseDisabled}
            data-action="close_sidebar_btn"
            sx={{ p: '3px', width: 30, height: 30, flex: '0 0 auto' }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
        {headerContent}
        {isLoading && <LinearProgress aria-label="Saving" sx={PROGRESS_SX} />}
      </Box>
      <Box ref={bodyRef} data-testid="side-panel-body" aria-busy={isLoading || undefined} sx={BODY_SX}>
        <Box sx={{ px: 3, pb: 3 }}>{children}</Box>
      </Box>
      {(actions || startActions) && (
        <Box sx={FOOTER_SX}>
          {startActions && <Box sx={START_ACTIONS_SX}>{startActions}</Box>}
          <Box sx={ACTIONS_SX}>{actions}</Box>
        </Box>
      )}
    </Box>
  );
};

export default SidebarFormLayout;
